import { NavigateFunction } from "react-router-dom";
import { ButtonTypes } from "../../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../../components/ui/Icon";
import { Panel } from "../../../../components/ui/Panel";
import I18n from "../../../../core/localization/I18n";
import { IModalContextModel } from "../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../core/modalZ/context/ModalContext";
import ToasterService, { IToasterService } from "../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../core/toaster/Toaster_model";
import { UiActionRenderers } from "../../../../core/uiAction/IUiAction";
import { BaseModel } from "../../../../core/util/BaseModel";
import { Enums } from "../../../../enums";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../services/api/v2/organisations/Organisations.api";
import ProjectsApi, { ProjectsApi as IProjectsApi } from "../../../../services/api/v2/projects/Projects.api";
import { getProjectFormFields, getProjectInitFormFields } from "../../forms/project/ProjectFormSection_data";
import { SingleFormModel } from "../../forms/singleFormModel/SingleForm_model";
import Pages from "../../../../routes/InsightRoutes";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";

export class CreateProjectViewModel extends BaseModel {
  initFormModel: SingleFormModel;
  formModel: SingleFormModel;
  projectProvider: IProjectsApi;
  organisationsProvider: IOrganisationsApi;
  httpProgress: IProgressIndicatorModel;
  programme: FP.Entities.IProgramme;
  orgId: number;
  toasterService: IToasterService;
  modalService: IModalContextModel;
  user: FP.Entities.IUser;
  refreshClaims: () => void;
  navigate: NavigateFunction;
  /**
   *
   */
  constructor(
    user: FP.Entities.IUser,
    refreshClaims: () => void,
    organisationId: number,
    navigate: NavigateFunction,
    programme?: FP.Entities.IProgramme
  ) {
    super();
    this.programme = programme;
    this.navigate = navigate;
    this.user = user;
    this.refreshClaims = refreshClaims;
    this.projectProvider = ProjectsApi;
    this.organisationsProvider = OrganisationsApi;
    this.httpProgress = ProgressIndicatorModel;
    this.toasterService = ToasterService;
    this.modalService = ModalContext;
    this.orgId = organisationId;
    this.initFormModel = new SingleFormModel();
    this.formModel = new SingleFormModel();
    this.setForms();
  }

  setForms = () => {
    this.initFormModel.formFields = getProjectInitFormFields(this.orgId, this.organisationsProvider);
    this.formModel.formFields = getProjectFormFields(this.orgId, this.organisationsProvider);
    this.formModel.actions = [
      {
        id: "createAnother",
        label: I18n.t("phrases.createAnother"),
        onAction: async () => {
          let res = await this.createProject();
          if (res) {
            this.toasterService
              .showSuccessToast()
              .setContent(
                <span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.project") })}</span>
              )
              .startTimer(TOASTER_TOAST_TIME.NORMAL);
            this.initFormModel.resetFields();
            this.formModel.resetFields();
          }
        },
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          type: ButtonTypes.OUTLINE_PRIMARY,
          className: "ml-auto"
        }
      },
      {
        id: "createProjectButton",
        label: I18n.t("phrases.createProject"),
        onAction: async () => {
          let res = await this.createProject();
          if (res) {
            this.navigate(Pages.projects.index.generateLink(this.orgId, res.id));
          }
        },
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          type: ButtonTypes.PRIMARY,
          className: "ml-2"
        }
      }
    ];
  };

  createProject = async (): Promise<FP.Entities.IProject> => {
    let projectFormEl = await this.formModel.submit();
    if (projectFormEl) {
      let formResult = {
        ...projectFormEl
      };

      this.httpProgress.showOverlay();
      let projectExists = await this.projectProvider.getFiltered(this.orgId, {
        filters: `name==${projectFormEl.name},lifecycleStatus==${Enums.LifecycleStatus.Active},organisationId==${this.orgId}`
      });
      if (projectExists && !projectExists.isError && projectExists.payload.length) {
        let confirmCreateProject = await this.confirmCreateProject(projectFormEl.name);
        if (!confirmCreateProject) return;
      }

      const res = await this.projectProvider.create(this.orgId, formResult as FP.Entities.IProject);
      if (!res || res.isError) {
        this.httpProgress.hideOverlay();
        return;
      }

      let result = await this.projectProvider.getById(this.orgId, res.payload.id);
      this.httpProgress.hideOverlay();
      if (!result || result.isError) return;
      return result.payload;
    }
  };

  confirmCreateProject = async (name: string): Promise<boolean> => {
    return new Promise(async resolve => {
      await this.modalService.showConfirmDialog(
        <h1 className="mt-4">{I18n.t("phrases.confirm")}</h1>,
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <Icon symbol={IconSymbols.AlertCircle} className="mr-2" />
              {I18n.t("warnings.createSameNameProject")}
            </div>
          </div>
          <div className="row">
            <div className="col">{I18n.t("warnings.confirmSameName", { name: name })}</div>
          </div>
        </div>,
        I18n.t("phrases.yes"),
        I18n.t("phrases.no"),
        {
          wrapWidth: "small",
          spacing: "small",
          position: "middle",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        async () => {
          this.httpProgress.hideOverlay();
          this.modalService.hide();
          resolve(true);
        },
        () => {
          this.httpProgress.hideOverlay();
          this.modalService.hide();
          resolve(false);
        }
      );
    });
  };
}
